import React, { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAMLFormSchema } from "@features/AMLModal/components/AMLFormModal/useAMLFormSchema";
import { Button } from "@components/Button";
import { BanknotesIcon } from "@components/icons";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useSubmitAMLMutation } from "@services/aml/mutations";
import {
  useCitizenshipsQuery,
  useDefaultCitizenshipsCountryQuery,
} from "@services/citizenships/queries";
import { useOverviewQuery } from "@services/overview/queries";
import { getCountryName, isNullOrUndefined } from "@utils/common";
import { formatDate } from "@utils/units";

interface IFormData {
  firstName: string;
  lastName: string;
  personalCode: string;
  citizenshipId: number;
  dateOfBirth: Date;
  residencyId: number;
  agreement: boolean;
}

interface IAMLFormModalProps extends IUseModalProps {
  isDefaultCountry: boolean;
  onFormSubmit?: () => void;
}

export const AMLFormModal: React.FC<IAMLFormModalProps> = ({
  isDefaultCountry,
  onFormSubmit,
  ...modalProps
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal(modalProps);
  const { data: citizenships = [] } = useCitizenshipsQuery();
  const { data: overview } = useOverviewQuery();
  const { data: defaultCitizenshipCountry } =
    useDefaultCitizenshipsCountryQuery();
  const { mutateAsync: submitAML, isPending } = useSubmitAMLMutation();
  const schema = useAMLFormSchema(isDefaultCountry);

  const { handleSubmit, control, setError } = useForm<IFormData>({
    defaultValues: {
      firstName: overview?.firstName ?? "",
      lastName: overview?.lastName ?? "",
      residencyId: undefined,
    },
    resolver: zodResolver(schema),
  });
  const { setApiErrors } = useApiErrorUtils(setError);

  const citizenshipOptions = useMemo(() => {
    const options = citizenships.map((ctz) => ({
      label: getCountryName(ctz.countryCode, i18n.language) ?? ctz.name,
      value: ctz.citizenshipId,
    }));

    if (!defaultCitizenshipCountry) return options;

    const defaultIndex = options.findIndex(
      (o) => o.value === defaultCitizenshipCountry.citizenshipId,
    );

    if (defaultIndex === -1) return options;

    const [defaultOption] = options.splice(defaultIndex, 1);

    if (!defaultOption) return options;

    return [defaultOption, ...options];
  }, [defaultCitizenshipCountry, citizenships, i18n.language]);

  const submitForm = async (data: IFormData) => {
    const response = await submitAML({
      firstName: data.firstName,
      lastName: data.lastName,
      verifyCorrectInformationSubmited: true,
      residencyId: data.residencyId,
      ...(isDefaultCountry
        ? {
            personalCode: data.personalCode,
            citizenshipId: defaultCitizenshipCountry?.citizenshipId,
          }
        : {
            citizenshipId: data.citizenshipId,
            dateOfBirth: formatDate(data.dateOfBirth ?? ""),
          }),
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      onFormSubmit?.();
    }
  };

  const [firstName, lastName] = useWatch({
    name: ["firstName", "lastName"],
    control,
  });

  const isDifferentName =
    firstName.trim() !== overview?.firstName?.trim() ||
    lastName.trim() !== overview?.lastName?.trim();

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BanknotesIcon }}
      title={t("AMLFormModal.title")}
      subtitle={
        isDefaultCountry
          ? t("AMLFormModal.subtitle.local")
          : t("AMLFormModal.subtitle.foreigner")
      }
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("AMLFormModal.actions.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(submitForm)}
            isLoading={isPending}
          >
            {t("AMLFormModal.actions.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-col gap-4">
          <FormInput
            name="firstName"
            control={control}
            label={t("AMLFormModal.form.fields.firstName.label")}
          />
          <FormInput
            name="lastName"
            control={control}
            label={t("AMLFormModal.form.fields.lastName.label")}
          />
          {isDefaultCountry ? (
            <FormInput
              name="personalCode"
              control={control}
              label={t("AMLFormModal.form.fields.personalCode.label")}
            />
          ) : (
            <>
              <FormAutocomplete
                name="citizenshipId"
                control={control}
                label={t("AMLFormModal.form.fields.citizenshipId.label")}
                options={citizenshipOptions}
              />
              <FormDatepicker
                name="dateOfBirth"
                control={control}
                label={t("AMLFormModal.form.fields.dateOfBirth.label")}
              />
            </>
          )}
          <FormAutocomplete
            name="residencyId"
            control={control}
            label={t("AMLFormModal.form.fields.residencyId.label")}
            options={citizenshipOptions}
          />
          <FormCheckbox
            name="agreement"
            label={t("AMLFormModal.form.fields.agreement.label")}
            control={control}
          />
        </form>
        {isDifferentName && (
          <Notification
            description={t("AMLFormModal.differentNameWarning")}
            color="red"
          />
        )}
      </div>
    </Modal>
  );
};
